import { isNil, get, map } from 'lodash-es';
import queryString from 'query-string';
import { config } from 'config';
import { extractTokenPayload } from 'utils/token';
import { RESPONSIVE_BREAK_POINTS, RESPONSIVE_RANGES } from 'utils/enums';
import { CtPrivacyRepository, CtTokenRepository } from 'common/api/models';
import { PermissionsEnum } from 'utils/permissions.enum';

export function optionalUrlParams(parameters) {
  const { sort } = parameters;
  return sort ? '?sort=' + sort : '';
}

export function getXPathForElement(element) {
  const idx = (sib, name) =>
    sib ? idx(sib.previousElementSibling, name || sib.localName) + (sib.localName === name) : 1;
  const segs = (elm) =>
    !elm || elm.nodeType !== 1
      ? ['']
      : elm.id && document.querySelector(`#${elm.id}`) === elm
        ? [`id("${elm.id}")`]
        : [...segs(elm.parentNode), `${elm.localName.toLowerCase()}[${idx(elm)}]`];
  return segs(element).join('/');
}

/**
 * @param {channelModel[]|navigationItemModel[]} channels
 */
export function getRootChannel(channels) {
  return channels.find((channel) => channel.parentId === null);
}

/**
 * @param {channelModel[]|navigationItemModel[]} channels - list of all channels
 * @param {*} parentChannel - parent channel object
 */
export function getSubChannels(channels, parentChannel) {
  return channels.filter((channel) => channel.parentId === parentChannel.id);
}

export function isParent(channels, parentChannel) {
  return channels.filter((channel) => channel.parentId === parentChannel.id).length > 0;
}

export function isRootChannel(channel) {
  return channel.parentId === -1 || channel.parentId === null;
}

export function prettyTime(str) {
  const sec = parseInt(str, 10) / 1000;
  const date = new Date(null);

  date.setSeconds(sec);

  const seconds = date.getUTCSeconds() < 10 ? '0' + date.getUTCSeconds() : date.getUTCSeconds();

  const minutes = date.getUTCMinutes() < 10 ? '0' + date.getUTCMinutes() : date.getUTCMinutes();

  const hours =
    date.getUTCHours() > 0 && date.getUTCHours() < 10
      ? '0' + date.getUTCHours() + ':'
      : date.getUTCHours() >= 10
        ? date.getUTCHours() + ':'
        : '';

  return hours + minutes + ':' + seconds;
}

/**
 * Query String props
 *
 * Converts GET url query string to react router "props.match.params" object.
 *
 * @param {{history: *, location: *, match:* }} props
 * @return {{history: *, location: *, match:* }}
 */
export const qsProps = (props) => {
  const queryParams = queryString.parse(props.location.search);
  if (Object.keys(queryParams).length > 0) {
    props.match.params = { ...props.match.params, ...queryParams };
  }
  return props;
};

/**
 * @param {ctTokenPayloadModel|null} ctTokenPayload
 * @return {boolean}
 */
export const isCtTokenExpired = (ctTokenPayload) => {
  if (ctTokenPayload === null) {
    return true;
  }
  const currentTime = Math.ceil(new Date().getTime() / 1000);
  const expiryTime = ctTokenPayload.exp;
  const expiryRatio = (expiryTime - currentTime) / config.ctTokenCheckInterval;

  return expiryRatio <= 0;
};

/**
 * @param {navigationItemModel} currentChannel
 * @param {navigationItemModel[]} channels
 * @return {navigationItemModel} parent channel
 */
export const getParent = (currentChannel, channels) => {
  const results = channels.filter((channel) => channel.id === currentChannel.parentId);

  return results.length ? results[0] : null;
};

/**
 * @param {navigationItemModel} channel
 * @param {navigationItemModel[]} channels
 * @param {number} level
 * @return {number} level in tree
 */
export const getLevelOfChannel = (channel, channels, level) => {
  const parent = getParent(channel, channels);
  if (parent) {
    return getLevelOfChannel(parent, channels, level + 1);
  }
  return level;
};

/**
 * @param {navigationItemModel} channel
 * @param {navigationItemModel[]} channels
 * @return {*}
 */
export const getChannelOption = (channel, channels) => {
  const level = getLevelOfChannel(channel, channels, 1);
  return {
    label: channel.title,
    value: channel.id,
    level: level,
    id: channel.id,
    parentId: channel.parentId,
  };
};

/**
 * Wrap url in text as hyperlink
 *
 * Converts link in string in <a href="link" target="target">link</a>
 *
 * @param {string} text
 * @param {boolean} new_window
 * @return {string}
 */
export const wrapURLs = (text, new_window = true) => {
  const URL_PATTERNS = [
    /( www\.)(.*?)( |<|$)/gm,
    /(^www\.)(.*?)( |<|$)/gm,
    /(^http:\/\/)(.*?)( |<|$)/gm,
    /( http:\/\/)(.*?)( |<|$)/gm,
    /(^https:\/\/)(.*?)( |<|$)/gm,
    /( https:\/\/)(.*?)( |<|$)/gm,
  ];
  const target = new_window === true || new_window == null ? '_blank' : '';

  return URL_PATTERNS.reduce(
    (acc, re) =>
      acc.replace(re, (a, b, c, d) => {
        let link = `${b.trim()}${c.trim()}`;
        let prefix = '';
        if (!link.match(/^http/)) {
          prefix = `https://`; // don't speculate - force https as today's web standard
        }
        return ` <a href="${prefix}${link}" target="${target}">${link}</a>${d}`;
      }),
    text
  );
};

/**
 * @return {boolean}
 */
export const isAdminUser = () => {
  const ctTokenPayload = extractTokenPayload();
  if (isNil(ctTokenPayload)) {
    return false;
  }
  return ctTokenPayload.admin;
};

export const tokenExpiredErrorHandle = (error) => {
  if (get(error, 'status') === 403 && get(error, 'json.message' === 'Expired token')) {
    window.location.reload(true);
  }
};

/**
 * @param {PermissionsEnum||string} permissionName
 * @return {boolean}
 */
export const hasPermission = (permissionName) => {
  if (CtPrivacyRepository.isPublic()) {
    return true;
  }

  if (!CtTokenRepository.fetch()) {
    return false;
  }

  const ctTokenPayload = extractTokenPayload();

  if (!('permissions' in ctTokenPayload)) {
    return false;
  }

  return ctTokenPayload.permissions.find((permission) => permission.name === permissionName) !== undefined;
};

/**
 * @return {boolean}
 */
export const hasChannelPermission = () => {
  if (CtPrivacyRepository.isPublic()) {
    return true;
  }

  if (!CtTokenRepository.fetch()) {
    return false;
  }

  const ctTokenPayload = extractTokenPayload();

  if (!('permissions' in ctTokenPayload)) {
    return false;
  }

  const channelPermission = ctTokenPayload.permissions.find(
    (permission) => permission.name === PermissionsEnum.CHANNEL_ACCESS
  );

  if (!channelPermission) {
    return true;
  }

  return Object.keys(channelPermission.data).length > 0;
};

export const sortingApiMap = (sorting) => {
  const sortMap = {
    relevance: { sk: 'relevance', so: 'desc' },
    display_date: { sk: 'display_date', so: 'desc' },
    title_asc: { sk: 'title', so: 'asc' },
    plays: { sk: 'plays', so: 'desc' },
    recently_added: { sk: 'recently_added', so: 'desc' },
    saved_date: { sk: 'saved_date', so: 'desc' },
    rating: { sk: 'rating', so: 'desc' },
  };

  const sortingOrder = sorting && sortMap[sorting].so ? `&sortingOrder=${sortMap[sorting].so}` : '';
  return sorting ? `&sortingKey=${sortMap[sorting].sk}${sortingOrder}` : `&sortingKey=display_date&sortingOrder=desc`;
};

/**
 *
 */
export const getWidth = () => {
  return document.getElementById('root').getBoundingClientRect().width || window.innerWidth;
};

/**
 * @returns {RESPONSIVE_RANGES}
 */
export const getResponsiveRange = () => {
  let width = document.getElementById('root').getBoundingClientRect().width || window.innerWidth;

  if (width < RESPONSIVE_BREAK_POINTS.MOBILE_LARGE) {
    return RESPONSIVE_RANGES.MOBILE_SMALL;
  }

  if (width < RESPONSIVE_BREAK_POINTS.I_FRAME) {
    return RESPONSIVE_RANGES.MOBILE_LARGE;
  }

  if (width < RESPONSIVE_BREAK_POINTS.DESKTOP_SMALL) {
    return RESPONSIVE_RANGES.I_FRAME;
  }

  if (width < RESPONSIVE_BREAK_POINTS.DESKTOP_MEDIUM) {
    return RESPONSIVE_RANGES.DESKTOP_SMALL;
  }

  if (width < RESPONSIVE_BREAK_POINTS.DESKTOP_LARGE) {
    return RESPONSIVE_RANGES.DESKTOP_MEDIUM;
  }

  if (width >= RESPONSIVE_BREAK_POINTS.DESKTOP_LARGE) {
    return RESPONSIVE_RANGES.DESKTOP_LARGE;
  }
};

/**
 * Returns list of valid ct channels from provide list of channels
 *
 * @param {{id: number}[]} channels - channels to verify
 * @param ctChannels - full list of CT channels (aka store.d_channels)
 * @return {number[]}
 */
export const filterCtChannels = (channels, ctChannels) => {
  return map(channels, (channel) => {
    if (ctChannels.find((ctChannel) => ctChannel.id === channel.id)) {
      return channel;
    }

    return false;
  }).filter((channel) => channel);
};

/**
 * @returns {boolean}
 */
export const isThreeVideos = (viewRange, isRelatedOn) => {
  const inDesktopLargeOrMediumRange =
    viewRange === RESPONSIVE_RANGES.DESKTOP_LARGE || viewRange === RESPONSIVE_RANGES.DESKTOP_MEDIUM;

  const inSmallOrIFrameRange = viewRange === RESPONSIVE_RANGES.DESKTOP_SMALL || viewRange === RESPONSIVE_RANGES.I_FRAME;

  return (inDesktopLargeOrMediumRange && !isRelatedOn) || inSmallOrIFrameRange;
};

/**
 * @returns {boolean}
 */
export const isFourVideos = (viewRange, isRelatedOn) => {
  return viewRange === RESPONSIVE_RANGES.DESKTOP_MEDIUM && isRelatedOn;
};

/**
 * @returns {boolean}
 */
export const isFiveVideos = (viewRange, isRelatedOn) => {
  return viewRange === RESPONSIVE_RANGES.DESKTOP_LARGE && isRelatedOn;
};

/**
 * @returns {number} number of videos per row
 */
export const getNumberOfVideosPerRow = (viewRange, isRelatedOn) => {
  if (isFiveVideos(viewRange, isRelatedOn)) {
    return 5;
  }
  if (isFourVideos(viewRange, isRelatedOn)) {
    return 4;
  }
  if (isThreeVideos(viewRange, isRelatedOn)) {
    return 3;
  }
  if (viewRange === RESPONSIVE_RANGES.MOBILE_LARGE) {
    return 2;
  }
  return 1;
};

/**
 * @returns {boolean} isMobileView
 */
export const isMobileView = (viewRange) => {
  return viewRange === RESPONSIVE_RANGES.MOBILE_LARGE || viewRange === RESPONSIVE_RANGES.MOBILE_SMALL;
};
